import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, ShieldCheck, ArrowRight, MessageSquare } from 'lucide-react';
import { fetchCollection, COMPANY_INFO } from '../data';

export default function TrustpilotPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTestimonials() {
      try {
        setLoading(true);
        const fetched = await fetchCollection('testimonials');
        setTestimonials(fetched);
      } catch (error) {
        console.error('Error loading testimonials:', error);
      } finally {
        setLoading(false);
      }
    }
    loadTestimonials();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-brand-dark/50 pt-20">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-blue border-t-transparent mb-4" />
        <p className="text-base">Indlæser anmeldelser...</p>
      </div>
    );
  }

  return (
    <div className="pt-20 space-y-16">
      {/* Hero Section */}
      <section className="relative bg-brand-deep text-white py-20 sm:py-28 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg"
            alt="To smilende glarmestre fra Energirude Eksperten foran firmabilen"
            className="w-full h-full object-cover object-[center_35%]"
          />
          {/* Nedtonet mørk/neutral gradient overlay for mindre kraftig blå baggrund og god tekstkontrast */}
          <div className="absolute inset-0 bg-slate-900/60 sm:bg-slate-900/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-900/70 to-slate-900/40" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-6">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs font-bold tracking-wider text-brand-light uppercase bg-white/15 border border-white/25 px-3.5 py-1.5 rounded-full inline-block backdrop-blur-xs shadow-2xs">
              Kundeanmeldelser
            </span>
            <span className="text-xs font-semibold text-white/80 bg-brand-blue/30 border border-brand-blue/40 px-3 py-1 rounded-full">
              Base i Smørum – dækker Egedal, Nordsjælland og store dele af Sjælland
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
            Vores kunders erfaringer
          </h1>
          <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto">
            Læs hvad vores kunder i Egedal, Nordsjælland og store dele af Sjælland siger om <Link to="/" className="text-white hover:text-brand-light underline font-semibold">Energirude Eksperten</Link>. Med base i Smørum sætter vi en stor ære i tilfredse kunder og kvalitetsarbejde.
          </p>

          {/* Rating Summary Card */}
          <div className="max-w-md mx-auto bg-white/95 backdrop-blur-sm border border-white/30 p-6 rounded-2xl shadow-xl flex items-center justify-around gap-6 pt-4">
            <div className="text-center">
              <span className="block text-4xl font-extrabold text-brand-deep">{COMPANY_INFO.trustpilotRating}</span>
              <span className="block text-xs text-brand-dark/60 font-semibold uppercase mt-1">Ud af 5 stjerner</span>
            </div>
            <div className="h-12 w-px bg-slate-200" />
            <div className="text-center">
              <div className="flex items-center justify-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-brand-green text-brand-green" />
                ))}
              </div>
              <span className="block text-xs text-brand-dark/70 font-semibold mt-1">
                Fremragende ({COMPANY_INFO.trustpilotCount} anmeldelser)
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Review Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {testimonials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="bg-white p-6 sm:p-8 rounded-2xl border border-brand-light shadow-sm space-y-4 flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-1">
                    {[...Array(t.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-brand-green text-brand-green" />
                    ))}
                  </div>
                  <div
                    className="text-sm sm:text-base text-brand-dark/80 leading-relaxed italic prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: t.text }}
                  />
                </div>
                <div className="pt-4 border-t border-brand-light flex items-center justify-between">
                  <span className="font-bold text-brand-deep text-sm sm:text-base">{t.name}</span>
                  <span className="text-xs text-brand-dark/40 font-semibold">Verificeret kunde</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-brand-dark/50">
            <MessageSquare className="w-12 h-12 mx-auto text-brand-dark/30 mb-3" />
            <p className="text-sm sm:text-base">Ingen anmeldelser fundet.</p>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-brand-deep text-white rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden border border-brand-blue/20 shadow-xl text-center space-y-6">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(36,119,199,0.15),transparent_60%)] pointer-events-none" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl mx-auto leading-tight">
            Vil du også have en professionel og tryg løsning?
          </h2>
          <p className="text-sm sm:text-base text-brand-light/80 max-w-2xl mx-auto">
            Book et gratis besøg af vores glarmester, der med base i Smørum dækker Egedal, Nordsjælland og store dele af Sjælland med præcis opmåling og rådgivning om den mest optimale løsning til din bolig. Det er 100% gratis og uforpligtende.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/gratis-besoeg"
              className="w-full sm:w-auto bg-brand-blue hover:bg-white hover:text-brand-deep text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 shadow-md"
            >
              Book gratis besøg nu
            </Link>
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
              className="w-full sm:w-auto border border-white/20 hover:bg-white/10 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
            >
              Ring på {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
